import { useCallback, useEffect, useContext, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import {
  selectCustomerInfo,
  selectIsGuestVerified,
  selectOrderId,
} from 'redux/cart/cart.selectors';
import { selectUserData, selectIsLoggedIn } from 'redux/user/user.selectors';
import {
  setCustomerInfo,
  setGuestVerified,
  sendGuestVerificationCode,
} from 'redux/cart/cart.actions';
import { customerSchema } from '../checkout.schema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { updateUserData } from 'redux/user/user.actions';
import { getSectionId } from '../checkout-section/checkout-section.hooks';
import CheckoutContext from '../checkout.context';
import { useCustomDialog } from 'components/custom-dialog/custom-dialog.hooks';
import {
  selectIsGuestCheckoutVerificationEnabled,
  selectGuestCheckoutVerificationDeliveryMethod,
} from 'redux/config/config.selectors';

const maskRecipient = (recipient, method) => {
  if (method === 'SMS') {
    const digits = recipient.replace(/\D/g, '');
    const lastFour = digits.slice(-4);
    return `***-***-${lastFour}`;
  }
  const atIndex = recipient.indexOf('@');
  if (atIndex < 1) return recipient;
  const local = recipient.slice(0, atIndex);
  const lastFour = local.slice(-4);
  return `***${lastFour}${recipient.slice(atIndex)}`;
};

export const useGuestVerification = () => {
  const dispatch = useDispatch();
  const orderId = useSelector(selectOrderId);
  const customerInfo = useSelector(selectCustomerInfo);
  const isGuestCheckoutVerificationEnabled = useSelector(
    selectIsGuestCheckoutVerificationEnabled,
  );
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isVerificationEnabled =
    isGuestCheckoutVerificationEnabled && !isLoggedIn;
  const deliveryMethod = useSelector(
    selectGuestCheckoutVerificationDeliveryMethod,
  );
  const { open, handleOpen, handleClose } = useCustomDialog();
  const [expirationMinutes, setExpirationMinutes] = useState(0);
  const [maskedRecipient, setMaskedRecipient] = useState('');

  const verificationFieldValue =
    deliveryMethod === 'SMS' ? customerInfo?.phoneNumber : customerInfo?.email;

  useEffect(() => {
    setMaskedRecipient('');
  }, [verificationFieldValue]);

  const sendCode = useCallback(
    async guestData => {
      const recipient =
        deliveryMethod === 'SMS' ? guestData.phoneNumber : guestData.email;
      const response = await dispatch(
        sendGuestVerificationCode(orderId, recipient),
      );
      if (!response) return;
      setMaskedRecipient(maskRecipient(recipient, deliveryMethod));
      setExpirationMinutes(response.expirationMinutes ?? 10);
      handleOpen();
    },
    [dispatch, orderId, deliveryMethod, handleOpen],
  );

  const resendCode = useCallback(async () => {
    const recipient =
      deliveryMethod === 'SMS'
        ? customerInfo?.phoneNumber
        : customerInfo?.email;
    const response = await dispatch(
      sendGuestVerificationCode(orderId, recipient),
    );
    if (!response) return;
    setMaskedRecipient(maskRecipient(recipient, deliveryMethod));
    setExpirationMinutes(response.expirationMinutes ?? 10);
  }, [dispatch, orderId, deliveryMethod, customerInfo]);

  return {
    isVerificationEnabled,
    deliveryMethod,
    isModalOpen: open,
    openModal: handleOpen,
    closeModal: handleClose,
    sendCode,
    resendCode,
    expirationMinutes,
    maskedRecipient,
  };
};

export const useCustomerSectionComponent = () => {
  const { t } = useTranslation();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const customerInfo = useSelector(selectCustomerInfo);
  const isGuestVerified = useSelector(selectIsGuestVerified);
  const userData = useSelector(selectUserData);
  const dispatch = useDispatch();
  const { validateSection } = useContext(CheckoutContext);
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(customerSchema),
  });

  const {
    sendCode,
    resendCode,
    isVerificationEnabled: isGuestCheckoutVerificationEnabled,
    deliveryMethod,
    isModalOpen,
    openModal,
    closeModal,
    expirationMinutes,
    maskedRecipient,
  } = useGuestVerification();

  const { handleSubmit } = methods;

  const submitCustomerInfo = () =>
    handleSubmit(
      async data => {
        if (isGuestCheckoutVerificationEnabled && isGuestVerified) {
          const verificationField =
            deliveryMethod === 'SMS' ? 'phoneNumber' : 'email';
          if (data[verificationField] !== customerInfo?.[verificationField]) {
            dispatch(setGuestVerified(false));
          }
        }
        await dispatch(setCustomerInfo(data));
        isLoggedIn &&
          (await dispatch(updateUserData({ ...userData, ...data })));
      },
      err => {
        throw err;
      },
    )();

  const handleGuestComplete = useCallback(
    async guestData => {
      if (isGuestCheckoutVerificationEnabled) await sendCode(guestData);
    },
    [sendCode, isGuestCheckoutVerificationEnabled],
  );

  useEffect(() => {
    if (!customerInfo && isLoggedIn && userData) {
      dispatch(
        setCustomerInfo({
          firstName: userData.firstName,
          lastName: userData.lastName,
          phoneNumber: userData.phoneNumber,
          email: userData.email,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo, dispatch, isLoggedIn, userData]);

  useEffect(() => {
    !!customerInfo &&
      validateSection(getSectionId(t('checkout.customer.title')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo]);

  useEffect(() => {
    if (isGuestVerified) {
      validateSection(getSectionId(t('checkout.customer.title')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuestVerified]);

  return {
    customerInfo,
    submitCustomerInfo,
    formMethods: methods,
    handleGuestComplete,
    isGuestCheckoutVerificationEnabled,
    deliveryMethod,
    isModalOpen,
    openModal,
    closeModal,
    sendCode,
    resendCode,
    expirationMinutes,
    maskedRecipient,
  };
};

export const useCustomerValidation = isVerificationCodeSent => {
  const { t } = useTranslation();
  const store = useStore();

  const validate = useCallback(async () => {
    try {
      await customerSchema.validate(selectCustomerInfo(store.getState()));
    } catch (err) {
      return err;
    }

    const isEnabled = selectIsGuestCheckoutVerificationEnabled(
      store.getState(),
    );
    const isLoggedIn = selectIsLoggedIn(store.getState());
    const isVerified = selectIsGuestVerified(store.getState());
    if (isEnabled && !isLoggedIn && !isVerified && isVerificationCodeSent) {
      return { message: t('checkout.guestVerification.verificationRequired') };
    }
  }, [store, isVerificationCodeSent, t]);

  return { validate };
};
